// src/screens/RegisterScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

// Google / Facebook / Apple sign-in needs each provider's SDK and client id.
// Hidden until those are set up; enable with REACT_APP_SOCIAL_LOGIN=true.
const SOCIAL_LOGIN_ENABLED = process.env.REACT_APP_SOCIAL_LOGIN === 'true';

// Social provider icons as SVG components
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
  </svg>
);

const EmailIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2"/>
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const RegisterScreen = () => {
  const { register, socialLogin, user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [step, setStep] = useState('choose'); // 'choose' or 'email'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/vehicles');
    }
  }, [user, navigate]);

  // Handle social login
  const handleSocialLogin = async (provider) => {
    setLoading(true);
    try {
      // Each handler returns the raw provider token, which the server verifies.
      let tokens = null;

      if (provider === 'google') {
        tokens = await handleGoogleLogin();
      } else if (provider === 'facebook') {
        tokens = await handleFacebookLogin();
      } else if (provider === 'apple') {
        tokens = await handleAppleLogin();
      }

      if (tokens) {
        await socialLogin(provider, tokens);
        navigate('/vehicles');
      }
    } catch (err) {
      logger.error('Social login failed', { message: err?.message });
      const msg = err.response?.data?.message || err.message || 'Login failed. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  // Google OAuth
  const handleGoogleLogin = () => {
    return new Promise((resolve, reject) => {
      // Check if Google API is loaded
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
          callback: (response) => {
            // Send the raw Google ID token; the server verifies it.
            resolve({ idToken: response.credential });
          },
        });
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            reject(new Error('Google login was cancelled'));
          }
        });
      } else {
        // Fallback: Open OAuth popup
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        const popup = window.open(
          `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&response_type=token&scope=email%20profile`,
          'Google Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );

        // Listen for callback
        const checkClosed = setInterval(() => {
          if (popup.closed) {
            clearInterval(checkClosed);
            reject(new Error('Google login was cancelled'));
          }
        }, 1000);
      }
    });
  };

  // Facebook OAuth
  const handleFacebookLogin = () => {
    return new Promise((resolve, reject) => {
      if (window.FB) {
        window.FB.login((response) => {
          if (response.authResponse) {
            // Send the raw Facebook access token; the server verifies it.
            resolve({ accessToken: response.authResponse.accessToken });
          } else {
            reject(new Error('Facebook login was cancelled'));
          }
        }, { scope: 'email,public_profile' });
      } else {
        // Fallback: Open OAuth popup
        const width = 500;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        window.open(
          `https://www.facebook.com/v18.0/dialog/oauth?client_id=${process.env.REACT_APP_FACEBOOK_APP_ID}&redirect_uri=${encodeURIComponent(window.location.origin + '/auth/callback')}&scope=email,public_profile`,
          'Facebook Login',
          `width=${width},height=${height},left=${left},top=${top}`
        );
        reject(new Error('Please configure Facebook SDK or use another login method'));
      }
    });
  };

  // Apple OAuth
  const handleAppleLogin = () => {
    return new Promise((resolve, reject) => {
      if (window.AppleID) {
        window.AppleID.auth.init({
          clientId: process.env.REACT_APP_APPLE_CLIENT_ID,
          scope: 'name email',
          redirectURI: window.location.origin + '/auth/callback',
          usePopup: true,
        });
        window.AppleID.auth.signIn()
          .then((response) => {
            // Send the raw Apple ID token; the server verifies it.
            resolve({ idToken: response.authorization.id_token });
          })
          .catch(reject);
      } else {
        reject(new Error('Apple Sign In is not available. Please use another login method.'));
      }
    });
  };

  // Handle email registration
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password, phone);
      navigate('/vehicles');
    } catch (err) {
      logger.error('Email registration failed', { message: err?.message });
      const msg = err.response?.data?.message || err.message || 'Registration failed. Please try again.';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  // Social button component
  const SocialButton = ({ provider, icon, label, onClick, className = '' }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={loading}
      className={`w-full flex items-center justify-center gap-3 px-4 py-3.5 rounded-2xl border border-app-border-light dark:border-app-border text-app-text-light dark:text-app-text font-medium transition-all duration-200 hover:shadow-md hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  return (
    <div className="flex flex-col min-h-screen lg:min-h-0 lg:py-12 lg:px-4 bg-gradient-to-br from-customer-accent-light/30 via-white to-customer-yellow-light/30 dark:from-app-bg dark:via-app-bg dark:to-app-bg customer-friendly">
      <div className="w-full max-w-md mx-auto">
        {/* Welcome Header */}
        <div className="text-center py-8 lg:py-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-app-text-light dark:text-app-text mb-2">
            {step === 'choose' ? 'Join the Adventure!' : 'Create Your Account'}
          </h1>
          <p className="text-app-text-secondary-light dark:text-app-text-secondary">
            {step === 'choose'
              ? 'Sign up to start test driving amazing cars'
              : 'Enter your details to get started'}
          </p>
        </div>
      </div>

      <div className="flex-1 p-6 lg:p-8 space-y-4 text-sm w-full max-w-md mx-auto bg-white dark:bg-app-bg-secondary rounded-3xl shadow-xl shadow-customer-accent/10 border border-app-border-light/50 dark:border-app-border">
        {step === 'choose' ? (
          <>
            {SOCIAL_LOGIN_ENABLED && (
              <>
            {/* Social Login Options */}
            <div className="space-y-3">
              <SocialButton
                provider="google"
                icon={<GoogleIcon />}
                label="Continue with Google"
                onClick={() => handleSocialLogin('google')}
                className="bg-white dark:bg-app-bg hover:bg-gray-50 dark:hover:bg-app-bg-tertiary"
              />

              <SocialButton
                provider="facebook"
                icon={<FacebookIcon />}
                label="Continue with Facebook"
                onClick={() => handleSocialLogin('facebook')}
                className="bg-white dark:bg-app-bg hover:bg-gray-50 dark:hover:bg-app-bg-tertiary"
              />

              <SocialButton
                provider="apple"
                icon={<AppleIcon />}
                label="Continue with Apple"
                onClick={() => handleSocialLogin('apple')}
                className="bg-white dark:bg-app-bg hover:bg-gray-50 dark:hover:bg-app-bg-tertiary"
              />
            </div>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-app-border-light/50 dark:border-app-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-app-bg-secondary text-app-text-secondary-light dark:text-app-text-secondary">
                  or
                </span>
              </div>
            </div>
              </>
            )}

            {/* Email Option */}
            <SocialButton
              provider="email"
              icon={<EmailIcon />}
              label="Continue with Email"
              onClick={() => setStep('email')}
              className="bg-gradient-to-r from-customer-accent to-customer-accent-hover text-white border-0 hover:from-customer-accent-hover hover:to-customer-accent-active"
            />

            {/* Login Link */}
            <div className="text-sm text-center text-app-text-secondary-light dark:text-app-text-secondary mt-6 pt-4 border-t border-app-border-light/50 dark:border-app-border">
              Already part of the crew?{' '}
              <Link to="/login" className="text-customer-teal font-semibold hover:text-customer-teal-hover transition-colors">
                Sign in here
              </Link>
            </div>
          </>
        ) : (
          <>
            {/* Back Button */}
            <button
              type="button"
              onClick={() => setStep('choose')}
              className="flex items-center gap-2 text-app-text-secondary-light dark:text-app-text-secondary hover:text-app-text-light dark:hover:text-app-text transition-colors mb-4"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span>Back to options</span>
            </button>

            {/* Email Registration Form */}
            <form onSubmit={handleEmailSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Your Name</label>
                <input
                  placeholder="What should we call you?"
                  className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-accent/50 focus:border-customer-accent transition-all"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-accent/50 focus:border-customer-accent transition-all"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Phone Number</label>
                <input
                  type="tel"
                  placeholder="(555) 555-0100"
                  className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-accent/50 focus:border-customer-accent transition-all"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>

              <div>
                <label className="text-sm font-medium text-app-text-light dark:text-app-text-secondary">Password</label>
                <input
                  type="password"
                  placeholder="Create a secure password"
                  className="mt-2 w-full px-4 py-3 rounded-2xl bg-app-bg-light dark:bg-app-bg border border-app-input-border-light dark:border-app-input-border text-sm outline-none focus:ring-2 focus:ring-customer-accent/50 focus:border-customer-accent transition-all"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="mt-4 w-full py-4 rounded-2xl bg-gradient-to-r from-customer-accent to-customer-accent-hover hover:from-customer-accent-hover hover:to-customer-accent-active text-white text-base font-bold shadow-lg shadow-customer-accent/30 hover:shadow-xl transition-all duration-300 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating Account...' : 'Start My Journey!'}
              </button>
            </form>

            {/* Login Link */}
            <div className="text-sm text-center text-app-text-secondary-light dark:text-app-text-secondary mt-4 pt-4 border-t border-app-border-light/50 dark:border-app-border">
              Already part of the crew?{' '}
              <Link to="/login" className="text-customer-teal font-semibold hover:text-customer-teal-hover transition-colors">
                Sign in here
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RegisterScreen;
